import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Flex, Form, Input, InputNumber, message, Radio, Result, Space, Spin, Switch, Typography } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import createDemoAccount, {
    CreateDemoAccountResponse,
    CreateDemoAccountFailure,
} from '../../../../domain/use_cases/demo/createDemoAccount';
import getAvailableDatasets from '../../../../domain/use_cases/demo/getAvailableDatasets';
import { ContextStatus, UserRole } from '@leedpulse-monorepo/enums';
import { getDemoAccountDataset, normalizeString } from '@leedpulse-monorepo/utils';
import { PlusCircleOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { formatDefinedDemoEmail, formatDefinedDemoEmailHost } from './definedDemoMail';
import DemoAccountFormItem from './DemoAccountFormItem';

import styles from './DemoAccount.module.css';

enum CreationMode {
    SINGLE = 'single',
    MULTIPLE = 'multiple',
}

type DemoAccountFormValues = {
    defined_mail_enabled?: boolean;
    defined_mail?: {
        mail_base?: string;
        mail_offset?: number;
        mail_domain?: string;
        country_code?: string;
    };
    accounts: Array<
        typeof getDemoAccountDataset extends () => infer R
        ? R extends { demo_account: infer D }
        ? D & {
            script?: string;
            dataset_filename?: string;
            new_dataset?: boolean;
            user: (D extends { user: infer U } ? U : never) & {
                email_manual_enabled?: boolean;
            };
        }
        : never
        : never
    >;
};
interface DemoAccountFormProps {
    companyId: string;
    onCancel: () => void;
}

export const WIDE_BORDER_WIDTH = 4;

/** Max rows in multi demo account form (UI + add buttons). */
export const MAX_DEMO_ACCOUNTS = 50;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DemoAccountForm: React.FC<DemoAccountFormProps> = ({ companyId, onCancel }) => {
    const demoAccountDataset = getDemoAccountDataset();

    const authContext = useContext(AuthenticationContext);

    const [availableDatasets, setAvailableDatasets] = useState<string[]>([]);
    const [datasetsLoaded, setDatasetsLoaded] = useState<boolean>(false);
    const [form] = Form.useForm<DemoAccountFormValues>();
    const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);
    const [createDemoAccountResponse, setCreateDemoAccountResponse] = useState<CreateDemoAccountResponse | null>(null);
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const [creationMode, setCreationMode] = useState<CreationMode>(CreationMode.SINGLE);
    const initialAccount = useMemo(() => demoAccountDataset.demo_account, [demoAccountDataset.demo_account]);
    const [accountResults, setAccountResults] = useState<Array<{ success: boolean; errorMessage?: string; user?: { email: string; password?: string } }>>([]);
    const [hasAutoDownloadedCsv, setHasAutoDownloadedCsv] = useState<boolean>(false);
    const [definedMailSuccessSummary, setDefinedMailSuccessSummary] = useState<string | null>(null);

    const definedMailEnabled = Form.useWatch('defined_mail_enabled', form) ?? false;

    const sortedDatasets = useMemo(() => {
        return [...availableDatasets].sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
    }, [availableDatasets]);

    const initDemoAccount = () => {
        const initialData = getDemoAccountDataset().demo_account;
        try {
            return structuredClone(initialData);
        } catch (_e) {
            return JSON.parse(JSON.stringify(initialData)) as typeof initialData;
        }
    };

    // Appends up to n demo rows, capped by MAX_DEMO_ACCOUNTS minus current list length.
    const addNAccounts = (
        add: (initial?: DemoAccountFormValues['accounts'][number]) => void,
        currentLength: number,
        n: number,
    ) => {
        const remainingSlots = Math.max(0, MAX_DEMO_ACCOUNTS - currentLength);
        const count = Math.min(Math.max(0, n), remainingSlots);
        for (let i = 0; i < count; i += 1) {
            const base = initDemoAccount();
            const withDefault = sortedDatasets[0]
                ? { ...base, dataset_filename: sortedDatasets[0], new_dataset: false }
                : { ...base, dataset_filename: undefined, new_dataset: true };
            add(withDefault);
        }
    };

    const downloadCsv = () => {
        const accounts = (createDemoAccountResponse?.data?.accounts ?? [])
            .slice()
            .sort((a, b) => a.index - b.index);

        const rows = [
            ['email', 'password', 'firstname', 'lastname'].join(','),
            ...accounts.map((a) => ([
                a.user.email,
                a.user.password ?? '',
                a.user.firstname ?? '',
                a.user.lastname ?? '',
            ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))),
        ].join('\n');

        const blob = new Blob([rows], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'demo-accounts.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const setMode = (mode: CreationMode) => {
        setCreationMode(mode);

        if (mode === CreationMode.SINGLE) {
            const accounts = form.getFieldValue(['accounts']) ?? [];
            const first = accounts?.[0] ?? initialAccount;
            // Mise a jour immuable : muter `first` touchait l'objet detenu par le
            // formulaire antd (et `initialAccount`, partage) au lieu d'en poser un neuf.
            form.setFieldValue(['accounts'], [
                { ...first, user: { ...first.user, email_manual_enabled: false } },
            ]);
        } else {
            const accounts = form.getFieldValue(['accounts']) ?? [];
            if (!Array.isArray(accounts) || accounts.length === 0) {
                form.setFieldValue(['accounts'], [initialAccount]);
            }
        }
    };

    const getErrorMessage = (err: unknown): string => {
        if (err instanceof CreateDemoAccountFailure) {
            return err.message;
        }
        if (err instanceof Error) {
            return err.message;
        }
        if (typeof err === 'string' && err.trim()) {
            return err;
        }
        return 'Une erreur inattendue s’est produite. Vous pouvez réessayer ou contacter l’assistance si le problème continue.';
    };

    const handleSubmit = async (values: DemoAccountFormValues) => {
        if (!authContext.authentication) {
            message.error('Vous devez être connecté pour effectuer cette action');
            return;
        }

        setSubmissionError(null);
        setCreateDemoAccountResponse(null);
        setHasAutoDownloadedCsv(false);
        setDefinedMailSuccessSummary(null);

        if (values.defined_mail_enabled) {
            const dm = values.defined_mail;
            const base = String(dm?.mail_base ?? '').trim();
            const domain = String(dm?.mail_domain ?? '').trim();
            const country = String(dm?.country_code ?? '').trim();
            if (!base || !domain || !country) {
                message.error('Remplissez la base mail, le domaine et le code pays.');
                return;
            }
            if (!/^[a-z]{2}$/i.test(country)) {
                message.error('Le code pays doit être 2 lettres (ex. fr).');
                return;
            }
        }

        for (let index = 0; index < (values.accounts ?? []).length; index += 1) {
            const user = values.accounts[index]?.user;
            if (values.defined_mail_enabled || !user?.email_manual_enabled) continue;

            const manualEmail = String(user.email ?? '').trim();
            if (!EMAIL_REGEX.test(manualEmail)) {
                message.error(`L'e-mail du compte ${index + 1} est invalide.`);
                return;
            }
        }

        const definedMailOffset = Math.max(0, Number(values.defined_mail?.mail_offset ?? 0) || 0);

        setStatus(ContextStatus.LOADING);

        try {
            const indexesToSend = (values.accounts ?? [])
                .map((_a, i) => i)
                .filter((i) => !accountResults[i]?.success);

            if (indexesToSend.length === 0) {
                message.info('Tous les comptes ont déjà été créés.');
                setStatus(ContextStatus.SUCCESS);
                return;
            }

            const payload = {
                company_id: companyId,
                accounts: indexesToSend.map((index) => {
                    const account = (values.accounts ?? [])[index];
                    let email: string;
                    let email_domain: string;

                    if (values.defined_mail_enabled && values.defined_mail) {
                        const mb = String(values.defined_mail.mail_base ?? '');
                        const md = String(values.defined_mail.mail_domain ?? '');
                        const cc = String(values.defined_mail.country_code ?? '');
                        email = formatDefinedDemoEmail(mb, index, md, cc, definedMailOffset);
                        email_domain = formatDefinedDemoEmailHost(md, cc);
                    } else if (account.user?.email_manual_enabled) {
                        email = String(account.user?.email ?? '').trim();
                        email_domain = email.split('@')[1] ?? '';
                    } else {
                        const firstname = String(account.user?.firstname ?? '');
                        const lastname = String(account.user?.lastname ?? '');
                        email_domain = String(account.user?.email_domain ?? '');
                        const computedLocal = `${normalizeString(firstname)}.${normalizeString(lastname)}`
                            .replace(/\.+/g, '.')
                            .replace(/^\./, '')
                            .replace(/\.$/, '');
                        email = `${computedLocal}@${email_domain}`;
                    }

                    return {
                        client_index: index,
                        ...account,
                        new_dataset: Boolean((account as { new_dataset?: unknown }).new_dataset),
                        user: {
                            ...account.user,
                            email,
                            email_authentication: email,
                            email_domain,
                        },
                    };
                }),
            };

            const response = await createDemoAccount(payload, authContext.authentication);
            setCreateDemoAccountResponse(response);
            if (!response.success) {
                const notOkMessage =
                    (response.statusReponse && String(response.statusReponse).trim()) ||
                    'Le serveur n’a pas confirmé la création du compte. Réessayez plus tard ou contactez l’assistance.';
                setSubmissionError(notOkMessage);
                setStatus(ContextStatus.ERROR);
                return;
            }

            const next = [...accountResults];
            for (const r of response.data?.results ?? []) {
                next[r.index] = {
                    success: Boolean(r.success),
                    errorMessage: r.errorMessage,
                    user: r.user ? { email: r.user.email, password: r.user.password } : next[r.index]?.user,
                };
            }
            setAccountResults(next);

            const allSuccess = next.length > 0 && next.every((r) => r?.success);
            if (allSuccess && values.defined_mail_enabled && values.defined_mail) {
                const mb = String(values.defined_mail.mail_base ?? '');
                const md = String(values.defined_mail.mail_domain ?? '');
                const cc = String(values.defined_mail.country_code ?? '');
                const firstIdx = indexesToSend[0] ?? 0;
                const lastIdx = indexesToSend[indexesToSend.length - 1] ?? firstIdx;
                const firstEmail = formatDefinedDemoEmail(mb, firstIdx, md, cc, definedMailOffset);
                const lastEmail = formatDefinedDemoEmail(mb, lastIdx, md, cc, definedMailOffset);
                setDefinedMailSuccessSummary(
                    indexesToSend.length > 1 && firstEmail !== lastEmail
                        ? `E-mails générés : de ${firstEmail} à ${lastEmail}`
                        : `E-mail généré : ${firstEmail}`,
                );
            } else {
                setDefinedMailSuccessSummary(null);
            }
            setStatus(allSuccess ? ContextStatus.SUCCESS : ContextStatus.IDLE);
        } catch (error) {
            setSubmissionError(getErrorMessage(error));
            setStatus(ContextStatus.ERROR);
        }
    };

    const handleCancel = () => {
        setStatus(ContextStatus.IDLE);
        setCreateDemoAccountResponse(null);
        setSubmissionError(null);
        form.resetFields();
        setAccountResults([]);
        setHasAutoDownloadedCsv(false);
        setDefinedMailSuccessSummary(null);
        onCancel();
    };

    const handleRetry = () => {
        setStatus(ContextStatus.IDLE);
        setSubmissionError(null);
        setCreateDemoAccountResponse(null);
        setAccountResults([]);
        setHasAutoDownloadedCsv(false);
        setDefinedMailSuccessSummary(null);
    };

    useEffect(() => {
        if (authContext.authentication) {
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setDatasetsLoaded(false);
            getAvailableDatasets(authContext.authentication).then((datasets) => {
                setAvailableDatasets(datasets);
                setDatasetsLoaded(true);
            }).catch((error) => {
                console.error(error);
                message.error('Une erreur est survenue lors de la récupération des datasets');
                setAvailableDatasets([]);
                setDatasetsLoaded(true);
            });
        }
    }, []);

    useEffect(() => {
        if (status !== ContextStatus.SUCCESS) return;
        if (creationMode !== CreationMode.MULTIPLE) return;
        if (hasAutoDownloadedCsv) return;
        if (!(createDemoAccountResponse?.data?.accounts?.length)) return;

        downloadCsv();
        // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
        // chargement declenche ici. Architecture Context + use case du repo.
        // eslint-disable-next-line react/set-state-in-effect
        setHasAutoDownloadedCsv(true);
    }, [status, creationMode, hasAutoDownloadedCsv, createDemoAccountResponse]);

    return (
        status === ContextStatus.IDLE ? (
            <Flex vertical gap={10}>
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    initialValues={{
                        defined_mail_enabled: false,
                        defined_mail: { mail_base: '', mail_offset: 0, mail_domain: '', country_code: '' },
                        accounts: [initialAccount],
                    }}
                    onFinish={handleSubmit}
                >
                    {authContext.authentication?.role === UserRole.owner ? (
                        <>
                            <Flex align="center" gap={16}>
                                <Radio.Group
                                    value={creationMode}
                                    defaultValue={creationMode}
                                    onChange={(e) => setMode(e.target.value as CreationMode)}
                                >
                                    <Radio.Button
                                        value={CreationMode.SINGLE}
                                    >
                                        <UserOutlined style={{ marginRight: 6, color: 'green' }} /> Classique</Radio.Button>
                                    <Radio.Button
                                        value={CreationMode.MULTIPLE}
                                    >
                                        <TeamOutlined style={{ marginRight: 6, color: 'red' }} /> Avancé</Radio.Button>
                                </Radio.Group>

                                {creationMode === CreationMode.MULTIPLE ? <Flex gap={8}>
                                    <Flex align="center" gap={8} wrap="wrap">
                                        <Typography.Text type="secondary">Définir un mail commun</Typography.Text>
                                        <Form.Item name="defined_mail_enabled" valuePropName="checked" noStyle>
                                            <Switch />
                                        </Form.Item>
                                    </Flex>
                                    {definedMailEnabled ? (
                                        <Flex align="center" gap={8} wrap="wrap">
                                            <Form.Item
                                                name={['defined_mail', 'mail_base']}
                                                rules={[{ required: true, message: 'Base mail requise.' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="demo" style={{ width: 160 }} />
                                            </Form.Item>
                                            <span>.</span>
                                            <Form.Item
                                                name={['defined_mail', 'mail_offset']}
                                                rules={[{ type: 'number', min: 0, message: 'Offset invalide.' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber min={0} precision={0} style={{ width: 88 }} />
                                            </Form.Item>
                                            <span>@</span>
                                            <Form.Item
                                                name={['defined_mail', 'mail_domain']}
                                                rules={[{ required: true, message: 'Domaine requis.' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="leedflow" style={{ width: 160 }} />
                                            </Form.Item>
                                            <span>.</span>
                                            <Form.Item
                                                name={['defined_mail', 'country_code']}
                                                rules={[
                                                    { required: true, message: 'Code pays requis.' },
                                                    { pattern: /^[a-zA-Z]{2,3}$/, message: '2 ou 3 lettres (ex. fr).' },
                                                ]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="fr" minLength={2} maxLength={3} style={{ width: 56 }} />
                                            </Form.Item>
                                        </Flex>
                                    ) : null}
                                </Flex> : null}
                            </Flex>
                            <Divider size='small' style={{ borderWidth: WIDE_BORDER_WIDTH }} />
                        </>
                    ) : null}
                    <Form.List name="accounts">
                        {(fields, { add, remove }) => {
                            const remainingSlots = Math.max(0, MAX_DEMO_ACCOUNTS - fields.length);
                            const showAddOne = remainingSlots >= 1;
                            const showAddTen = remainingSlots >= 10;
                            // Avoid duplicating the "+10" shortcut when exactly 10 slots remain, and "+1" when only one slot.
                            const showAddRemaining =
                                remainingSlots >= 2 && remainingSlots !== 10;

                            return (
                                <Flex vertical style={{ overflowY: "auto", maxHeight: "65vh", paddingRight: "1rem" }}>
                                    {fields.map((field, index) => (
                                        <React.Fragment key={field.key}>
                                            <DemoAccountFormItem
                                                field={field}
                                                index={index}
                                                fieldsCount={fields.length}
                                                form={form}
                                                datasets={sortedDatasets}
                                                defaultDatasetFilename={sortedDatasets[0]}
                                                datasetsLoaded={datasetsLoaded}
                                                creationMode={creationMode}
                                                accountResult={accountResults[index]}
                                                onRemove={() => remove(field.name)}
                                            />
                                        </React.Fragment>))}

                                    {creationMode === CreationMode.MULTIPLE && (showAddOne || showAddTen || showAddRemaining) ? (
                                        <>
                                            <Divider size='small' style={{ borderWidth: WIDE_BORDER_WIDTH }} />
                                            <Flex justify='flex-start' gap={10} wrap="wrap">
                                                {showAddOne ? (
                                                    <Button
                                                        htmlType="button"
                                                        onClick={() => addNAccounts(add, fields.length, 1)}
                                                        icon={<PlusCircleOutlined />}
                                                    >
                                                        +1 compte
                                                    </Button>
                                                ) : null}

                                                {showAddTen ? (
                                                    <Button
                                                        htmlType="button"
                                                        onClick={() => addNAccounts(add, fields.length, 10)}
                                                        icon={<PlusCircleOutlined />}
                                                    >
                                                        +10 comptes
                                                    </Button>
                                                ) : null}

                                                {showAddRemaining ? (
                                                    <Button
                                                        htmlType="button"
                                                        onClick={() =>
                                                            addNAccounts(add, fields.length, remainingSlots)
                                                        }
                                                        icon={<PlusCircleOutlined />}
                                                    >
                                                        +{remainingSlots} compte{remainingSlots > 1 ? 's' : ''}
                                                    </Button>
                                                ) : null}
                                            </Flex>
                                        </>
                                    ) : null}
                                </Flex>
                            );
                        }}
                    </Form.List>
                    <Flex justify='flex-end' gap={10} style={{ marginTop: "0.25rem" }}>
                        <Button type="default" htmlType="button" onClick={handleCancel}>
                            Annuler
                        </Button>
                        <Button type="primary" htmlType="submit">
                            Créer
                        </Button>
                    </Flex>
                </Form>
            </Flex>
        ) : (
            <div className={styles.stateRoot} style={{ maxWidth: 520, margin: '0 auto' }}>
                {status === ContextStatus.LOADING ? (
                    <Result
                        icon={<Spin size="large" />}
                        title="Création en cours"
                        subTitle={
                            <Space direction="vertical" size="small" style={{ maxWidth: 420 }}>
                                <Typography.Paragraph className={styles.stateParagraph}>
                                    Votre compte de démonstration est en train d&apos;être généré. Cela peut prendre
                                    <strong> quelques minutes</strong>.
                                </Typography.Paragraph>
                                <Typography.Paragraph type="secondary" className={styles.stateParagraph}>
                                    Vous recevrez une notification sur <strong>Slack</strong> dès qu&apos;il sera prêt, avec
                                    les <strong>identifiants de connexion</strong>.
                                </Typography.Paragraph>
                            </Space>
                        }
                    />
                ) : status === ContextStatus.SUCCESS ? (
                    <Result
                        status="success"
                        title="Compte(s) de démonstration prêt(s)"
                        subTitle={
                            <Flex vertical gap={10}>
                                {creationMode === CreationMode.SINGLE ? (
                                    <>
                                        <Typography.Text type="secondary" className={styles.stateParagraph}>
                                            Vous pouvez utiliser les identifiants ci-dessous pour vous connecter.
                                        </Typography.Text>
                                        {createDemoAccountResponse?.data?.accounts?.[0]?.user ? (
                                            <div className={styles.credentialBox}>
                                                <div>
                                                    <Typography.Text type="secondary">E-mail</Typography.Text>
                                                    <div className={styles.credentialValue}>
                                                        <Typography.Text copyable>{createDemoAccountResponse.data.accounts[0].user.email}</Typography.Text>
                                                    </div>
                                                </div>
                                                {createDemoAccountResponse.data.accounts[0].user.password ? (
                                                    <div>
                                                        <Typography.Text type="secondary">Mot de passe</Typography.Text>
                                                        <div className={styles.credentialValue}>
                                                            <Typography.Text copyable>{createDemoAccountResponse.data.accounts[0].user.password}</Typography.Text>
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    <Typography.Text type="secondary" className={styles.stateParagraph}>
                                        Tous les comptes ont été créés. Le CSV a été téléchargé avec les identifiants, si le téléchargement n'a pas fonctionné, vous pouvez le télécharger manuellement.
                                    </Typography.Text>
                                )}
                            </Flex>
                        }
                        extra={[
                            ...(definedMailSuccessSummary
                                ? [
                                    <Typography.Paragraph key="defined-mail" type="secondary" style={{ width: '100%', marginBottom: 4 }}>
                                        {definedMailSuccessSummary}
                                    </Typography.Paragraph>,
                                ]
                                : []),
                            <Button key="csv" type="primary" onClick={downloadCsv}>
                                Télécharger le CSV
                            </Button>,
                            <Button key="close" onClick={handleCancel}>
                                Fermer
                            </Button>,
                        ]}
                    />
                ) : (
                    <Result
                        status="error"
                        title="La création n’a pas abouti"
                        subTitle={
                            <Typography.Paragraph className={styles.errorMessage}>
                                {submissionError || 'Vérifiez votre connexion et réessayez, ou contactez l’assistance.'}
                            </Typography.Paragraph>
                        }
                        extra={[
                            <Button key="retry" onClick={handleRetry}>
                                Modifier et réessayer
                            </Button>,
                            <Button key="close" type="primary" htmlType="button" onClick={handleCancel}>
                                Fermer
                            </Button>,
                        ]}
                    />
                )}
            </div>
        )
    );
};

export default DemoAccountForm;